/**
 * Agent Adapter - Sprint 31
 *
 * Communication adapter for mainnet deployment.
 * Uses @dfinity/agent to communicate directly with IC canisters.
 *
 * This adapter is used when the app is deployed to mainnet (*.ic0.app, *.icp0.io).
 */

import { HttpAgent, Actor, ActorSubclass } from '@dfinity/agent';
import { Principal } from '@dfinity/principal';
import { IDL } from '@dfinity/candid';
import {
    CommunicationAdapter,
    CanisterCallOptions,
    CanisterCallError,
    TimeoutError,
    AdapterNotInitializedError
} from './types';
import { environment } from '../../config/environment';
import { getCanisterId, CanisterName } from '../../config/canister-ids';

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_TIMEOUT = 30000; // 30 seconds

// ============================================================================
// IDL Factories - Imported from generated declarations
// ============================================================================

// Import IDL factories from declarations (generated by dfx generate)
import { idlFactory as adaIdlFactory } from '@declarations/ada/ada.did.js';
import { idlFactory as awsDiscoveryIdlFactory } from '@declarations/aws_discovery_integration/aws_discovery_integration.did.js';
import { idlFactory as blockchainStorageIdlFactory } from '@declarations/blockchain_storage/blockchain_storage.did.js';
import { idlFactory as claudeProxyIdlFactory } from '@declarations/claude_proxy/claude_proxy.did.js';
import { idlFactory as credentialManagerIdlFactory } from '@declarations/credential_manager/credential_manager.did.js';
import { idlFactory as discoveryDoctorIdlFactory } from '@declarations/discovery_doctor/discovery_doctor.did.js';
import { idlFactory as discoveryEngineIdlFactory } from '@declarations/discovery_engine/discovery_engine.did.js';
import { idlFactory as iiAgentIdlFactory } from '@declarations/ii_agent/ii_agent.did.js';
import { idlFactory as knowledgeBaseIdlFactory } from '@declarations/knowledge_base/knowledge_base.did.js';
import { idlFactory as llmProxyIdlFactory } from '@declarations/llm_proxy/llm_proxy.did.js';
import { idlFactory as serviceMapperIdlFactory } from '@declarations/service_mapper/service_mapper.did.js';
import { idlFactory as userManagerIdlFactory } from '@declarations/user_manager/user_manager.did.js';
import { idlFactory as vectorDbIdlFactory } from '@declarations/vector_db/vector_db.did.js';

/**
 * Map of canister names to their IDL factories
 */
const IDL_FACTORIES: Record<string, IDL.InterfaceFactory> = {
    'ada': adaIdlFactory,
    'aws_discovery_integration': awsDiscoveryIdlFactory,
    'blockchain_storage': blockchainStorageIdlFactory,
    'claude_proxy': claudeProxyIdlFactory,
    'credential_manager': credentialManagerIdlFactory,
    'discovery_doctor': discoveryDoctorIdlFactory,
    'discovery_engine': discoveryEngineIdlFactory,
    'ii_agent': iiAgentIdlFactory,
    'knowledge_base': knowledgeBaseIdlFactory,
    'llm_proxy': llmProxyIdlFactory,
    'service_mapper': serviceMapperIdlFactory,
    'user_manager': userManagerIdlFactory,
    'vector_db': vectorDbIdlFactory
};

/**
 * Get IDL factory for a canister
 * @throws Error if canister name not found
 */
function getIdlFactory(canisterName: string): IDL.InterfaceFactory {
    const factory = IDL_FACTORIES[canisterName];
    if (!factory) {
        throw new Error(`IDL factory not found for canister: ${canisterName}`);
    }
    return factory;
}

// ============================================================================
// Agent Adapter Implementation
// ============================================================================

/**
 * Agent adapter for mainnet communication
 *
 * Uses @dfinity/agent HttpAgent to make direct canister calls
 * through IC boundary nodes.
 */
export class AgentAdapter implements CommunicationAdapter {
    readonly name = 'AgentAdapter';
    private agent: HttpAgent | null = null;
    private initialized = false;
    private actorCache: Map<string, ActorSubclass<any>> = new Map();

    constructor(private readonly host?: string) {
        this.host = host || environment.icHost;
    }

    // ========================================================================
    // CommunicationAdapter Implementation
    // ========================================================================

    async initialize(): Promise<void> {
        try {
            // Create the HTTP agent
            this.agent = new HttpAgent({
                host: this.host,
                // On mainnet, don't fetch root key (it's built into the agent)
                // On local, we need to fetch it
                ...(environment.mode === 'local' ? {} : {})
            });

            // For local development, fetch the root key
            if (environment.mode === 'local') {
                await this.agent.fetchRootKey();
            }

            this.initialized = true;
            console.log(`✅ ${this.name} initialized - host: ${this.host}`);

        } catch (error) {
            console.error(`❌ ${this.name} initialization failed:`, error);
            throw error;
        }
    }

    isReady(): boolean {
        return this.initialized && this.agent !== null;
    }

    async call<T = any>(options: CanisterCallOptions): Promise<T> {
        if (options.isQuery) {
            return this.query<T>(options);
        }
        return this.update<T>(options);
    }

    async query<T = any>(options: CanisterCallOptions): Promise<T> {
        return this.makeAgentCall<T>(options, 'query');
    }

    async update<T = any>(options: CanisterCallOptions): Promise<T> {
        return this.makeAgentCall<T>(options, 'update');
    }

    // ========================================================================
    // Private Methods
    // ========================================================================

    /**
     * Make a call using the IC agent
     */
    private async makeAgentCall<T>(
        options: CanisterCallOptions,
        callType: 'query' | 'update'
    ): Promise<T> {
        if (!this.isReady()) {
            throw new AdapterNotInitializedError(this.name);
        }

        const { canisterName, methodName, args, timeout } = options;
        const effectiveTimeout = timeout || DEFAULT_TIMEOUT;

        if (environment.debug) {
            console.log(`🔧 [${this.name}] ${callType} ${canisterName}.${methodName}`);
            console.log(`   Args:`, args);
        }

        try {
            // Get canister ID
            const canisterId = getCanisterId(canisterName as CanisterName);

            // Get or create actor
            const actor = await this.getActor(canisterName.toString(), canisterId);

            // Make the call with timeout
            const result = await this.withTimeout(
                this.callActor(actor, methodName, args, callType),
                effectiveTimeout,
                canisterName.toString(),
                methodName
            );

            if (environment.debug) {
                console.log(`✅ [${this.name}] Response:`, result);
            }

            return result as T;

        } catch (error: any) {
            if (error instanceof TimeoutError || error instanceof CanisterCallError) {
                throw error;
            }

            throw new CanisterCallError(
                error.message || 'Unknown error',
                canisterName.toString(),
                methodName,
                error
            );
        }
    }

    /**
     * Get or create an actor for a canister
     */
    private async getActor(
        canisterName: string,
        canisterId: string
    ): Promise<ActorSubclass<any>> {
        const cacheKey = `${canisterName}:${canisterId}`;

        if (!this.actorCache.has(cacheKey)) {
            const idlFactory = getIdlFactory(canisterName);

            const actor = Actor.createActor(idlFactory, {
                agent: this.agent!,
                canisterId: Principal.fromText(canisterId)
            });

            this.actorCache.set(cacheKey, actor);
        }

        return this.actorCache.get(cacheKey)!;
    }

    /**
     * Call an actor method
     */
    private async callActor(
        actor: ActorSubclass<any>,
        methodName: string,
        args: any[],
        _callType: 'query' | 'update'
    ): Promise<any> {
        // Note: Actor methods are automatically generated from IDL
        // For generic IDL, we need to use raw agent calls
        // This is a simplified implementation

        const method = actor[methodName];

        if (typeof method !== 'function') {
            throw new Error(`Method "${methodName}" not found on actor`);
        }

        // Call the method with arguments
        return method(...args);
    }

    /**
     * Wrap a promise with timeout
     */
    private async withTimeout<T>(
        promise: Promise<T>,
        timeoutMs: number,
        canisterName: string,
        methodName: string
    ): Promise<T> {
        return Promise.race([
            promise,
            new Promise<never>((_, reject) => {
                setTimeout(() => {
                    reject(new TimeoutError(canisterName, methodName, timeoutMs));
                }, timeoutMs);
            })
        ]);
    }

    // ========================================================================
    // Public Utilities
    // ========================================================================

    /**
     * Get the underlying agent (for advanced use cases)
     */
    getAgent(): HttpAgent | null {
        return this.agent;
    }

    /**
     * Clear the actor cache (useful after identity change)
     */
    clearActorCache(): void {
        this.actorCache.clear();
    }

    /**
     * Set the identity on the agent (for authentication)
     */
    async setIdentity(identity: any): Promise<void> {
        if (!this.agent) {
            throw new AdapterNotInitializedError(this.name);
        }

        // Create new agent with identity
        this.agent = new HttpAgent({
            host: this.host,
            identity
        });

        // Fetch root key for local
        if (environment.mode === 'local') {
            await this.agent.fetchRootKey();
        }

        // Clear actor cache as identity changed
        this.clearActorCache();

        console.log(`🔑 [${this.name}] Identity updated`);
    }
}

// ============================================================================
// Factory Function
// ============================================================================

/**
 * Create an agent adapter with default configuration
 */
export function createAgentAdapter(): AgentAdapter {
    return new AgentAdapter(environment.icHost);
}
